/**
 * HTML Sanitization utilities using DOMPurify.
 * Use these functions to sanitize any HTML content before rendering with dangerouslySetInnerHTML.
 */
import DOMPurify from 'dompurify';

/**
 * Default allowed tags for rich text content (from TipTap editor, emails, etc.)
 */
const DEFAULT_ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a',
  'blockquote',
  'code', 'pre',
  'hr',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img',
  'div', 'span',
];

/**
 * Default allowed attributes
 */
const DEFAULT_ALLOWED_ATTR = [
  'href', 'target', 'rel',
  'src', 'alt', 'width', 'height',
  'class', 'style',
  'colspan', 'rowspan',
  // `dir` carries per-paragraph text direction (RTL/LTR/auto) from the editor.
  // DOMPurify's ALLOWED_ATTR replaces the default allowlist, so without this
  // the direction set on each block would be stripped when displayed.
  'dir',
];

/**
 * CSS properties permitted inside inline `style` attributes for general rich
 * text content. MUST stay in sync with the backend `ALLOWED_CSS_PROPERTIES`
 * list in `shared/utils/sanitize.py`. Deliberately excludes the `background`
 * shorthand and `background-image` (both carry `url()`, a data-exfiltration
 * vector) — only `background-color` is allowed. See the backend file for the
 * full rationale. This is the render-surface (defense-in-depth) mirror of the
 * backend persist-layer filter.
 */
const ALLOWED_CSS_PROPERTIES = new Set<string>([
  'color', 'background-color',
  'font-family', 'font-size', 'font-weight', 'font-style',
  'text-align', 'text-decoration', 'line-height',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'border', 'border-radius', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-color', 'border-style', 'border-width',
  'width', 'max-width', 'min-width',
  'height', 'max-height', 'min-height',
  'display', 'vertical-align',
  'box-shadow',
]);

/**
 * Filter an inline `style` value down to ALLOWED_CSS_PROPERTIES. Declarations
 * whose property is not allowlisted — or whose value carries a `url(...)` or
 * `expression(...)` payload — are dropped. Mirrors the backend CSS sanitizer so
 * a hostile `style` (e.g. `position: fixed` overlays, `background-image: url()`
 * beacons) cannot reach the render surface.
 *
 * The PROPERTY allowlist is the primary guard: no allowlisted property carries a
 * URL, so url()/expression() exfiltration is structurally impossible regardless
 * of the value check. The value-level `url(`/`expression(` regex is a secondary
 * belt-and-suspenders — it is intentionally simple and not escape-proof (CSS
 * hex escapes like `\28` would slip past it), so do NOT add a URL-bearing
 * property to the allowlist relying on it to neutralise the URL.
 */
function filterStyleAttribute(style: string): string {
  return style
    .split(';')
    .map((declaration) => {
      const separator = declaration.indexOf(':');
      if (separator === -1) return null;
      const property = declaration.slice(0, separator).trim().toLowerCase();
      const value = declaration.slice(separator + 1).trim();
      if (!property || !value) return null;
      if (!ALLOWED_CSS_PROPERTIES.has(property)) return null;
      if (/url\s*\(|expression\s*\(/i.test(value)) return null;
      return `${property}: ${value}`;
    })
    .filter((declaration): declaration is string => declaration !== null)
    .join('; ');
}

/**
 * Sanitize HTML content for safe rendering.
 * Removes potentially dangerous elements and attributes (scripts, event handlers, etc.)
 * and filters inline `style` to a safe CSS-property allowlist (mirrors backend).
 *
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) return '';

  // Scope the style-filtering hook to this call only — the email/template
  // sanitizers below intentionally keep permissive inline styling. DOMPurify
  // hooks are global, but sanitize() is synchronous, so add → sanitize → remove
  // in a try/finally never leaks the hook to other callers.
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName === 'style') {
      const filtered = filterStyleAttribute(data.attrValue);
      // Drop the attribute entirely when nothing survives, rather than leaving
      // an empty `style=""` on the element.
      if (!filtered) {
        data.keepAttr = false;
        return;
      }
      data.attrValue = filtered;
    }
  });
  try {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: DEFAULT_ALLOWED_TAGS,
      ALLOWED_ATTR: DEFAULT_ALLOWED_ATTR,
      // Force all links to open in new tab with noopener
      ADD_ATTR: ['target'],
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    });
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
}

/**
 * Sanitize HTML for email display.
 * More permissive than default to handle various email formats.
 */
export function sanitizeEmailHtml(dirty: string): string {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      ...DEFAULT_ALLOWED_TAGS,
      'center', 'font', 'sup', 'sub',
    ],
    ALLOWED_ATTR: [
      ...DEFAULT_ALLOWED_ATTR,
      'align', 'valign', 'bgcolor', 'color', 'size', 'face',
      'border', 'cellpadding', 'cellspacing',
    ],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    // Allow data URIs for embedded images (common in emails)
    ADD_DATA_URI_TAGS: ['img'],
  });
}

/**
 * Sanitize HTML for admin template preview.
 * Allows style tags for email template styling.
 */
export function sanitizeTemplateHtml(dirty: string): string {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      ...DEFAULT_ALLOWED_TAGS,
      'style', 'center', 'font', 'sup', 'sub',
    ],
    ALLOWED_ATTR: [
      ...DEFAULT_ALLOWED_ATTR,
      'align', 'valign', 'bgcolor', 'color', 'size', 'face',
      'border', 'cellpadding', 'cellspacing',
    ],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    ADD_DATA_URI_TAGS: ['img'],
  });
}

/**
 * Strip all HTML tags, returning only text content.
 * Useful for previews or plain text fallbacks.
 */
export function stripHtml(dirty: string): string {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
}
